/**
 * HaircutAnalyticsPage — admin-only telemetry dashboard for the Volatile-5d
 * 0.55× haircut. Answers the question raised in iter127 review: is 0.55×
 * the right coefficient, or are we over/under-tuning?
 *
 * Reads /api/regime/admin/haircut-analytics (admin-gated) and renders the
 * per-strategy breakdown, cut-magnitude distribution, and verdict-change rate.
 *
 * Scope constraint (iter127): admin-only, read-only, no user-facing exposure.
 * Not linked from the primary nav.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, TrendingDown, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const DAY_OPTIONS = [7, 30, 90, 180, 365];

export default function HaircutAnalyticsPage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setData(null); setErr(null);
    api.get(`/regime/admin/haircut-analytics?days=${days}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || e?.message); });
    return () => { cancelled = true; };
  }, [days, isAdmin]);

  if (!isAdmin) {
    return (
      <div data-testid="haircut-analytics-forbidden" className="flex items-center justify-center py-16 px-4">
        <div className="border max-w-md w-full p-6 text-center"
             style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <Lock className="w-4 h-4 mx-auto mb-2" style={{ color: "#F59E0B" }} />
          <div className="text-sm font-bold mb-1">Admin only</div>
          <div className="text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
            The haircut analytics dashboard is restricted to platform administrators.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="haircut-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4" style={{ color: "#F59E0B" }} />
            <h1 className="text-lg font-bold">Haircut Analytics</h1>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold"
                  style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B",
                           border: "1px solid rgba(245,158,11,0.35)" }}>
              admin only
            </span>
          </div>
          <div className="text-[11px] mt-1" style={{ color: "var(--hoip-text-secondary)" }}>
            Empirical view of the Volatile-5d 0.55× haircut. Read-only telemetry from <span className="font-mono-data">db.ranker_haircut_events</span>.
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Window:</span>
          {DAY_OPTIONS.map(d => (
            <button key={d}
              data-testid={`window-${d}d-btn`}
              onClick={() => setDays(d)}
              className="px-2 py-1 border font-bold"
              style={{
                background: days === d ? "rgba(245,158,11,0.15)" : "var(--hoip-card)",
                borderColor: days === d ? "#F59E0B" : "var(--hoip-border)",
                color: days === d ? "#F59E0B" : "var(--hoip-text-secondary)",
              }}>{d}d</button>
          ))}
        </div>
      </div>

      {err && (
        <div className="border p-3 text-[11px]" style={{
          background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.35)",
          color: "#EF4444"
        }}>
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {err}
        </div>
      )}

      {!data && !err && (
        <div className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</div>
      )}

      {data && (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Total haircuts" value={data.total_haircuts} testid="kpi-total" />
            <Kpi label="Unique tickers" value={data.unique_tickers} testid="kpi-tickers" />
            <Kpi label="Unique strategies" value={data.unique_strategies} testid="kpi-strategies" />
            <Kpi label="Verdict change rate"
                 value={`${data.verdict_change_rate}%`}
                 hint="How often a cut actually flipped GO→CAUTION vs. just trimming score"
                 testid="kpi-verdictrate" />
            <Kpi label="Mean cut (points)"
                 value={data.cut_magnitude.mean}
                 hint={`p95=${data.cut_magnitude.p95}  max=${data.cut_magnitude.max}`}
                 testid="kpi-meancut" />
          </div>

          {/* Coefficient guidance */}
          {data.total_haircuts > 0 && (
            <div data-testid="coefficient-note" className="border p-3 text-[11px]"
                 style={{ background: "rgba(59,130,246,0.06)", borderColor: "rgba(59,130,246,0.30)" }}>
              <div className="font-bold mb-1" style={{ color: "#3B82F6" }}>Coefficient guidance</div>
              <div style={{ color: "var(--hoip-text-secondary)" }}>
                {data.score_multiplier_note}
              </div>
            </div>
          )}

          {/* Coefficient sensitivity — replays audit rows at 0.45× / 0.55× / 0.65×.
              Iter130 answers: "would a harder coefficient have flipped these
              events to CAUTION?" without needing a separate backtest run. */}
          {data.coefficient_sensitivity && (
            <div data-testid="coefficient-sensitivity"
                 className="border p-3"
                 style={{ borderColor: "var(--hoip-border)" }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold">Coefficient sensitivity replay</div>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  Simulated GO→CAUTION flips over the same audit rows
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {Object.entries(data.coefficient_sensitivity).map(([label, s]) => {
                  const isLive = Math.abs(s.coefficient - 0.55) < 0.001;
                  return (
                    <div key={label}
                         data-testid={`sensitivity-${label}`}
                         className="border p-2"
                         style={{
                           borderColor: isLive ? "#F59E0B" : "var(--hoip-border)",
                           background: isLive ? "rgba(245,158,11,0.06)" : "var(--hoip-card)",
                         }}>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-bold font-mono-data">{label}</div>
                        {isLive && (
                          <span className="text-[8px] px-1 rounded-sm font-bold"
                                style={{ background: "#F59E0B", color: "#111" }}>LIVE</span>
                        )}
                      </div>
                      <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
                        {s.flipped_go_to_caution} of {s.flipped_go_to_caution + s.stayed_go} GO events flipped
                      </div>
                      <div className="text-lg font-bold font-mono-data mt-1">
                        {s.flip_rate_pct}%
                      </div>
                    </div>
                  );
                })}
              </div>
              {data.coefficient_recommendation && (
                <div data-testid="coefficient-recommendation" className="text-[10px] mt-2"
                     style={{ color: "var(--hoip-text-secondary)" }}>
                  <span className="font-bold" style={{ color: "#F59E0B" }}>Model output: </span>
                  {data.coefficient_recommendation}
                </div>
              )}
            </div>
          )}

          {/* Per-strategy breakdown */}
          <div className="border" style={{ borderColor: "var(--hoip-border)" }}>
            <div className="px-3 py-2 border-b text-[11px] font-bold"
                 style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
              Per-strategy breakdown
            </div>
            <table className="w-full text-[11px] font-mono-data" data-testid="per-strategy-table">
              <thead>
                <tr style={{ color: "var(--hoip-text-tertiary)" }}>
                  <th className="text-left px-3 py-1.5">Strategy</th>
                  <th className="text-right px-3">Events</th>
                  <th className="text-right px-3">Avg cut</th>
                  <th className="text-right px-3">Verdict changes</th>
                  <th className="text-right px-3">Tickers</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_strategy)
                  .sort(([, a], [, b]) => b.count - a.count)
                  .map(([sid, s]) => (
                    <tr key={sid} data-testid={`row-${sid}`} className="border-t"
                        style={{ borderColor: "var(--hoip-border)" }}>
                      <td className="px-3 py-1.5">{sid}</td>
                      <td className="text-right px-3">{s.count}</td>
                      <td className="text-right px-3">-{s.avg_cut}</td>
                      <td className="text-right px-3">{s.verdict_change_count}</td>
                      <td className="text-right px-3">{s.unique_tickers}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Verdict transitions + Instant vs 5d agreement */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border p-3" style={{ borderColor: "var(--hoip-border)" }}>
              <div className="text-[11px] font-bold mb-2">Verdict transitions</div>
              {Object.keys(data.verdict_changes || {}).length === 0
                ? <div className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                    No verdict changes recorded — the 0.55× cut trimmed scores but didn&apos;t flip any GO→CAUTION.
                    That&apos;s a signal the coefficient may be too gentle.
                  </div>
                : Object.entries(data.verdict_changes).map(([k, v]) =>
                    <div key={k} className="flex justify-between text-[11px] py-0.5">
                      <span className="font-mono-data">{k}</span>
                      <span className="font-mono-data font-bold">{v}</span>
                    </div>)}
            </div>
            <div className="border p-3" style={{ borderColor: "var(--hoip-border)" }}>
              <div className="text-[11px] font-bold mb-2">Instant vs 5d agreement on cuts</div>
              {(() => {
                const a = data.instant_vs_5d_agreement || {};
                const total = (a.agree || 0) + (a.disagree || 0);
                if (!total) return <div className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No data</div>;
                return (
                  <>
                    <div className="flex justify-between text-[11px] py-0.5">
                      <span>Instant == 5d (agree)</span>
                      <span className="font-mono-data">{a.agree || 0} ({Math.round((a.agree || 0) / total * 100)}%)</span>
                    </div>
                    <div className="flex justify-between text-[11px] py-0.5">
                      <span>Instant != 5d (disagree)</span>
                      <span className="font-mono-data">{a.disagree || 0} ({Math.round((a.disagree || 0) / total * 100)}%)</span>
                    </div>
                    <div className="text-[9px] mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
                      High disagreement % here means we&apos;re cutting when the fast (Instant) horizon has already moved on.
                      That&apos;s expected in transitional regimes but sustained &gt;50% disagreement suggests the 5d window is too slow.
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Top tickers */}
          {data.top_tickers?.length > 0 && (
            <div className="border p-3" style={{ borderColor: "var(--hoip-border)" }}>
              <div className="text-[11px] font-bold mb-2">Top affected tickers</div>
              <div className="flex flex-wrap gap-2">
                {data.top_tickers.map(([t, c]) => (
                  <span key={t} className="text-[10px] px-2 py-0.5 rounded-sm border font-mono-data"
                        style={{ borderColor: "var(--hoip-border)" }}
                        data-testid={`ticker-${t}`}>
                    {t} <span style={{ color: "var(--hoip-text-tertiary)" }}>· {c}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, testid }) {
  return (
    <div className="border p-2.5" data-testid={testid}
         style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
      <div className="text-[9px] uppercase tracking-wider" style={{ color: "var(--hoip-text-tertiary)" }}>
        {label}
      </div>
      <div className="text-lg font-bold font-mono-data mt-1">{value}</div>
      {hint && <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{hint}</div>}
    </div>
  );
}
